import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Footer from './Footer';

const BlogList = () => {
  const blogPosts = [
    {
      id: 'panda-story',
      title: 'The Story Behind Panda',
      excerpt: 'How 26K users came together in 15 days, and the people who made it possible',
      date: 'January 2025',
      category: 'Story',
      readTime: '5 min read',
      featured: true
    },
    {
      id: 'accessibility-focus',
      title: 'Why We Build for Accessibility',
      excerpt: 'A deep dive into our mission to help visually impaired users regain digital independence.',
      date: 'February 2025',
      category: 'Mission',
      readTime: '4 min read',
      featured: false
    },
  ];

  const featuredPost = blogPosts.find(post => post.featured);
  const regularPosts = blogPosts.filter(post => !post.featured);

  return (
    <div className="blog-list-page">
      {/* Blog Header */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            className="blog-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="back-link-wrapper">
              <Link to="/" className="back-link">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></svg>
                Back to Home
              </Link>
            </div>
            <div className="blog-meta">
              <span className="blog-category">Blog</span>
            </div>
            <h1 className="blog-title">
              Stories from Panda
            </h1>
            <p className="blog-subtitle">
              Insights, stories, and lessons from building the future of voice assistants
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="blog-content">
        <div className="container">

          {/* --- FEATURED POST --- */}
          {featuredPost && (
            <motion.div
              key={featuredPost.id}
              className="blog-post-card featured-card"
              style={{ marginTop: '-100px' }} // The "pro tweak" to pull it up
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link to={`/blog/${featuredPost.id}`} className="blog-post-link">
                <div className="post-meta">
                  <span className="post-category">{featuredPost.category}</span>
                  <span className="post-date">{featuredPost.date}</span>
                </div>
                <h2 className="post-title featured-title">{featuredPost.title}</h2>
                <p className="post-excerpt featured-excerpt">{featuredPost.excerpt}</p>
                <div className="post-footer">
                  <span className="read-time">{featuredPost.readTime}</span>
                  <span className="read-more">Read more →</span>
                </div>
              </Link>
            </motion.div>
          )}

          {/* --- REGULAR POSTS GRID --- */}
          <h2 className="more-stories-title">More Stories</h2>
          <div className="blog-posts-grid">
            {regularPosts.map((post, index) => (
              <motion.div
                key={post.id}
                className="blog-post-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link to={`/blog/${post.id}`} className="blog-post-link">
                  <div className="post-meta">
                    <span className="post-category">{post.category}</span>
                    <span className="post-date">{post.date}</span>
                  </div>
                  <h2 className="post-title">{post.title}</h2>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <div className="post-footer">
                    <span className="read-time">{post.readTime}</span>
                    <span className="read-more">Read more →</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      <Footer />

      <style jsx>{`
        .blog-list-page {
          min-height: 100vh;
          /* This lists all backgrounds. The radial gradients go on top,
            and the main dark gradient goes on the bottom as the base layer.
          */
          background: 
            radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.05) 0%, transparent 50%),
            var(--gradient-dark); /* The base dark gradient */

          /* This is the key: it stops the background from repeating */
          background-attachment: fixed; 
          
          color: var(--text-primary);
        }

        .back-link-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 32px;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          border-radius: 100px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          font-size: 0.9rem;
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }

        .back-link:hover {
          color: white;
          background: rgba(255,255,255,0.12);
          border-color: rgba(255,255,255,0.25);
          transform: translateX(-4px);
        }

        .back-link svg {
          transition: transform 0.3s ease;
        }

        .back-link:hover svg {
          transform: translateX(-3px);
        }

        .blog-hero {
          padding: 120px 0 80px;
          /* We add extra padding at the bottom (180px) to make space 
             for the overlapping card. 100px (overlap) + 80px (original padding) */
          padding-bottom: 180px; 
          position: relative;
        }

        .blog-header {
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .blog-meta {
          display: none; /* Hides the "Blog" glass card */
        }

        .blog-title {
          font-size: 4.5rem; 
          font-weight: 700; /* Bolder to match the logo */
          margin-bottom: 24px;
          line-height: 1.2;
        }

        .blog-subtitle {
          font-size: 1.25rem;
          color: var(--text-secondary);
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto;
        }

        .blog-content {
          padding: 80px 0;
          /* NO BACKGROUND - This makes it seamless */
          position: relative;
          z-index: 2; /* Ensures content is above hero gradient */
        }

        .blog-posts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .blog-post-card {
          /* This is the new frosted greyish tint */
          background: rgba(255, 255, 255, 0.1); 
            
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 20px;
          padding: 32px;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .blog-post-card:hover {
          transform: translateY(-5px);
          border-color: rgba(255, 255, 255, 0.5);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
        
        .featured-card {
          margin-bottom: 64px;
          border-width: 2px;
          border-color: rgba(255, 255, 255, 0.4);
          /* A slightly stronger greyish tint */
          background: rgba(255, 255, 255, 0.15); 
        }

        .featured-title {
          font-size: 2.25rem;
        }

        .featured-excerpt {
          font-size: 1.125rem;
          color: var(--text-secondary);
        }

        .more-stories-title {
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }

        .blog-post-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .post-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .post-category {
          padding: 4px 12px;
          background: rgba(139, 92, 246, 0.2);
          border: 1px solid rgba(139, 92, 246, 0.3);
          border-radius: 12px;
          font-size: 0.8rem;
          color: var(--primary-color);
          font-weight: 500;
        }

        .post-date {
          font-size: 0.9rem;
          color: var(--text-secondary);
        }

        .post-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 16px;
          line-height: 1.3;
          color: var(--text-primary);
        }

        .post-excerpt {
          font-size: 1rem;
          line-height: 1.6;
          color: var(--text-secondary);
          margin-bottom: 24px;
        }

        .post-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .read-time {
          font-size: 0.9rem;
          color: var(--text-muted);
        }

        .read-more {
          font-size: 0.9rem;
          color: var(--primary-color);
          font-weight: 600;
          transition: color 0.3s ease;
        }

        .blog-post-card:hover .read-more {
          color: var(--secondary-color);
        }

        @media (max-width: 768px) {
          .blog-hero {
            padding: 80px 0 60px;
            /* Remove the large padding on mobile, overlap isn't needed */
            padding-bottom: 120px; 
          }

          .blog-title {
            font-size: 3rem; 
            font-weight: 700;
          }

          .blog-subtitle {
            font-size: 1.125rem;
          }

          .blog-content {
            padding: 60px 0;
          }
          
          .featured-card {
            padding: 24px;
            margin-bottom: 48px;
            margin-top: -80px; /* Smaller overlap for mobile */
          }

          .featured-title {
            font-size: 1.75rem;
          }

          .featured-excerpt {
            font-size: 1rem;
          }
          
          .blog-posts-grid {
            grid-template-columns: 1fr;
            gap: 30px;
            /* Remove padding, let cards handle it */
            padding: 0; 
          }
          
          /* Container padding for mobile */
          .container {
            padding: 0 20px;
          }

          .blog-post-card {
            padding: 24px;
          }

          .more-stories-title {
            font-size: 1.5rem;
            /* Remove padding, container handles it */
          }

          .post-title {
            font-size: 1.25rem;
          }

          .post-excerpt {
            font-size: 0.9rem;
          }
        }
      `}</style>
    </div >
  );
};

export default BlogList;